import React from 'react';
import PropTypes from 'prop-types';
import {Map} from 'immutable';
import {callAction} from 'client/framework';
import {history} from 'client/history';
import {BasicTable, WithTooltip, Icon, LoadingSpinnerLong} from '@albert-io/atomic';

/* eslint-disable import/no-named-as-default,import/order */
import AssignmentDrilldownStore from '../AssignmentDrilldownStore';
import HealthIndicator from 'generic/HealthIndicator/HealthIndicator.react';
import MasteryFlag from 'generic/MasteryFlag/MasteryFlag.react';
import StudentsStore from './StudentsStore';
import StudentsActions from './StudentsActions';
import FixedTooltip from 'generic/FixedTooltip/FixedTooltip.react';
import {
  SubmissionIndicator,
  SubmissionResetModal
} from 'client/Classrooms/shared/SubmissionIndicator/SubmissionIndicator.react';
import {stats} from 'lib/StatsUtil';
import {ReportsLinkBanner} from 'client/Reports/ReportsLinkBanner.react';

const CONFIRM_MODAL_WRAPPER_STORE_NAME = 'AssignmentWindowConfirmWrapperStore';
const CONFIRM_MODAL_STORE_NAME = 'AssignmentWindowConfirmStore';

export default class Students extends React.Component {
  static propTypes = {
    params: PropTypes.object
  };

  componentDidMount() {
    callAction(
      StudentsActions.INITIALIZE_STUDENT_LIST,
      new Map({
        classroomId: this.props.params.classId,
        assignmentId: this.props.params.assignmentId
      })
    );
  }

  getCompletedIndicator(student) {
    const studentId = student.getId();
    const assignment = AssignmentDrilldownStore.getActiveAssignment();
    const assignmentId = assignment.getId();
    const tooltipStoreName = `SubmittedStatusStore--${studentId}--${assignmentId}`;
    const submittedTimestamp = student.getAssignmentRelationships(assignmentId).getSubmitted();
    const totalGuesses =
      student.getMeta().getAssignmentCountOfCorrectGuesses() +
      student.getMeta().getAssignmentCountOfIncorrectGuesses();
    const isComplete = totalGuesses === assignment.getMeta().getCountOfQuestions();
    return (
      <SubmissionIndicator
        assignment={assignment}
        isComplete={isComplete}
        student={student}
        submissionResetModalStoreName={CONFIRM_MODAL_WRAPPER_STORE_NAME}
        submittedTimestamp={submittedTimestamp}
        tooltipStoreName={tooltipStoreName}
        windowConfirmStoreName={CONFIRM_MODAL_STORE_NAME}
      />
    );
  }

  makeHealthTooltip(student) {
    const assignment = AssignmentDrilldownStore.getActiveAssignment();
    const correctAnswers = student.getMeta().getAssignmentCountOfCorrectGuesses();
    const totalAnswered = correctAnswers + student.getMeta().getAssignmentCountOfIncorrectGuesses();
    const assignmentSize = assignment.getMeta().getCountOfQuestions();
    const totalAnsweredString = `(${totalAnswered}/${assignmentSize}) - ${stats.percentage(
      totalAnswered,
      assignmentSize
    )}%`;
    const answeredCorrectlyString = `(${correctAnswers}/${assignmentSize}) - ${stats.percentage(
      correctAnswers,
      assignmentSize
    )}%`;
    return (
      <div className='insight-table__question-answer-badge-tooltip'>
        <div className='insight-table__question-answer-badge-tooltip-datum'>
          <dt className='insight-table__question-answer-badge-tooltip-term'>Total Answered</dt>
          <dd className='insight-table__question-answer-badge-tooltip-definition'>
            {totalAnsweredString}
          </dd>
        </div>
        <div className='insight-table__question-answer-badge-tooltip-datum'>
          <dt className='insight-table__question-answer-badge-tooltip-term'>Answered Correctly</dt>
          <dd className='insight-table__question-answer-badge-tooltip-definition'>
            {answeredCorrectlyString}
          </dd>
        </div>
      </div>
    );
  }

  makeRowForStudent(student) {
    const correctAnswers = student.getMeta().getAssignmentCountOfCorrectGuesses();
    const incorrectAnswers = student.getMeta().getAssignmentCountOfIncorrectGuesses();
    const assignment = AssignmentDrilldownStore.getActiveAssignment();
    const assignmentSize = assignment.getMeta().getCountOfQuestions();
    const questionsAnswered = correctAnswers + incorrectAnswers;
    const hasStarted = questionsAnswered > 0;
    const grade = hasStarted ? `${stats.percentage(correctAnswers, assignmentSize)}%` : 'N/A';
    const accuracy = hasStarted ? `${stats.percentage(correctAnswers, questionsAnswered)}%` : 'N/A';
    const correctPercentage = stats.percentage(correctAnswers, assignmentSize);
    const strugglingPercentage = stats.percentage(incorrectAnswers, assignmentSize);
    const emptyPercentage = stats.percentage(assignmentSize - questionsAnswered, assignmentSize);
    const classroomId = AssignmentDrilldownStore.readData('classroomId');
    return (
      <BasicTable.Row
        key={student.getId()}
        className='insight-table__row insight-table__row--no-border'
      >
        <BasicTable.Cell
          className='insight-table__student-info'
          onClick={() => {
            const to = `/classes/${classroomId}/assignments/${
              this.props.params.assignmentId
            }/student-detail/${student.getId()}/view`;
            history.pushState(null, to);
          }}
        >
          <div className='insight-table__student-info-name' data-gtu={student.getId()}>
            {student.getDisplayName('l, f', student.getLastName(), student.getEmail())}
          </div>
        </BasicTable.Cell>
        <BasicTable.Cell>{this.getCompletedIndicator(student)}</BasicTable.Cell>
        <BasicTable.Cell contentType='graphic' align='center'>
          <div className='insight-table__health-indicator'>
            <HealthIndicator
              strugglingPercentage={strugglingPercentage}
              excellingPercentage={correctPercentage}
              emptypercentage={emptyPercentage}
            />
            <FixedTooltip type='dark'>{this.makeHealthTooltip(student)}</FixedTooltip>
          </div>
        </BasicTable.Cell>
        <BasicTable.Cell align='center'>
          <span>{grade}</span>
          <span data-gtg={student.getId()} className='u-display_none' aria-hidden='true'>
            {correctAnswers}
          </span>
        </BasicTable.Cell>
        <BasicTable.Cell align='center'>{accuracy}</BasicTable.Cell>
        <BasicTable.Cell align='center'>
          <MasteryFlag accuracy={correctPercentage} hasStarted={hasStarted} />
        </BasicTable.Cell>
      </BasicTable.Row>
    );
  }

  render() {
    if (
      !AssignmentDrilldownStore.hasActiveAssignment() ||
      !StudentsStore.hasStudentStats() ||
      !AssignmentDrilldownStore.hasAssignmentStats()
    ) {
      return (
        <div className='assignment-drilldown__students u-display_flex u-align-items_center u-justify-content_center u-pad-tb_5'>
          <LoadingSpinnerLong />
        </div>
      );
    }

    // TODO: Fold this into the StudentsStore
    const activeAssignmentStudents = AssignmentDrilldownStore.getActiveAssignmentStudents();
    const tableHeadings = [
      {
        label: 'Name',
        align: 'left'
      },
      {
        label: 'Submission Status',
        align: 'left'
      },
      {
        label: 'Completion',
        align: 'center'
      },
      {
        label: 'Grade',
        align: 'center'
      },
      {
        label: 'Accuracy',
        align: 'center'
      },
      {
        label: 'Status',
        align: 'center'
      }
    ];

    const classroomId = AssignmentDrilldownStore.readData('classroomId');
    const assignmentId = AssignmentDrilldownStore.getActiveAssignment().getId();

    return (
      <div className='assignment-drilldown__students'>
        <ReportsLinkBanner
          className='u-mar_2'
          link={`/reports/classrooms/${classroomId}/assignments/${assignmentId}/students`}
        />
        <BasicTable>
          <BasicTable.Head>
            <BasicTable.Row>
              {tableHeadings.map((heading, key) => {
                if (heading.label === 'Grade') {
                  return (
                    <BasicTable.Header align={heading.align} key={key}>
                      {heading.label}
                      <WithTooltip
                        content={
                          <div className='insight-table__header-tooltip'>
                            <em>Grade</em> is the percentage of questions correct based on the{' '}
                            <em>total</em> number of questions in the assignment.
                          </div>
                        }
                        placement='top'
                      >
                        <Icon
                          icon='question-circle'
                          color='black'
                          emphasis='lowest'
                          className='u-mar-l_1'
                        />
                      </WithTooltip>
                    </BasicTable.Header>
                  );
                }

                if (heading.label === 'Accuracy') {
                  return (
                    <BasicTable.Header align={heading.align} key={key}>
                      {heading.label}
                      <WithTooltip
                        content={
                          <div className='insight-table__header-tooltip'>
                            <em>Accuracy</em> is the percentage of questions correct based on the
                            number of questions the student has answered in the assignment.
                          </div>
                        }
                        placement='top'
                      >
                        <Icon
                          icon='question-circle'
                          color='black'
                          emphasis='lowest'
                          className='u-mar-l_1'
                        />
                      </WithTooltip>
                    </BasicTable.Header>
                  );
                }

                return (
                  <BasicTable.Header align={heading.align} key={key}>
                    {heading.label}
                  </BasicTable.Header>
                );
              })}
            </BasicTable.Row>
          </BasicTable.Head>
          <BasicTable.Body>
            {activeAssignmentStudents
              .sortBy((student) => [
                student.getLastName().toLowerCase(),
                student.getFirstName().toLowerCase()
              ])
              .map((student) => this.makeRowForStudent(student))}
          </BasicTable.Body>
        </BasicTable>
        <SubmissionResetModal
          storeName={CONFIRM_MODAL_WRAPPER_STORE_NAME}
          windowConfirmStoreName={CONFIRM_MODAL_STORE_NAME}
        />
      </div>
    );
  }
}
